/**
 * Copyright (C) Boost commerce
 * This file is part of commercial Boost commerce projects
 *
 * This file can not be copied and/or distributed without the express
 * permission of Boost commerce
 *
 * Created on Thu Mar 23 2023 11:10:55
 */
// @flow

export const formatMoney = (
  money: number | string,
  format: string,
  withTrailingZeros: boolean,
  showCentAsSuperscript: boolean,
  removePriceDecimal: boolean,
  removeDecimalPoint: boolean
) => {
  if (!['number', 'string'].includes(typeof money)) return '';
  if (!format) format = window.boostSDAppConfig?.shop?.money_format;
  if (format === 'money_with_currency')
    format = window.boostSDAppConfig?.shop?.money_format_with_currency;
  if (typeof money === 'string') {
    money = money.replace('.', '');
  }

  // Get type format money
  const typeFormatMoney = getTypeFormatMoney(format);

  // Format money by type
  let moneyFormat = formatMoneyByType(typeFormatMoney, money);

  // Format money with trailing zeros
  if (!withTrailingZeros) moneyFormat = moneyFormat.replace(/((,00)|(\.00))$/g, '');

  // Remove decimal
  if (removePriceDecimal) {
    moneyFormat = removeDecimal(
      moneyFormat,
      window.boostSDAppConfig?.generalSettings?.decimalDelimiter
    );
  }

  // Format money with cent as superscript
  if (showCentAsSuperscript) {
    const [ints, cents] = separateDecimalMoney(moneyFormat, typeFormatMoney, removeDecimalPoint);
    if (cents) moneyFormat = `${ints}<sup>${cents}</sup>`;
  }

  // Replace config format with money format
  return compileMoneyWithConfigFormat(moneyFormat, format);
};

const stripHtml = (string: string) => {
  return string?.replace(/<[^>]*>?/gi, '').replaceAll('&#36;', '$');
};

const compileMoneyWithConfigFormat = (moneyFormat: string, format: string) => {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  // eslint-disable-next-line no-template-curly-in-string
  let formatString = format || '${{amount}}';

  formatString = stripHtml(formatString).replace(placeholderRegex, moneyFormat);

  return formatString;
};

const getDecimalPoint = (format: string) => {
  switch (format) {
    case 'amount':
    case 'amount_with_space_separator_no_comma':
      return '.';
    case 'amount_with_comma_separator':
      return ',';
    case 'amount_no_decimals':
    case 'amount_no_decimals_with_comma_separator':
    case 'amount_no_decimals_with_space_separator':
    default:
      return null;
  }
};

const removeDecimal = (value: string | number, delimiter: string) => {
  const reg = new RegExp('(\\' + delimiter + '\\d+)+', 'gi');
  return ('' + value).replace(reg, '');
};

const separateDecimalMoney = (
  moneyFormat: string | number,
  format: string,
  removeDecimalPoint: boolean
) => {
  const decimalPoint = getDecimalPoint(format);
  if (!decimalPoint) return [`${moneyFormat}`];

  const [ints, cents] = `${moneyFormat}`.split(decimalPoint);

  return cents ? [ints, removeDecimalPoint ? `${cents}` : `${decimalPoint}${cents}`] : [ints];
};

const formatMoneyByType = (type: string, money: number | string) => {
  function defaultOption(opt: number | string, def: number | string) {
    return typeof opt == 'undefined' ? def : opt;
  }

  function formatWithDelimiters(
    number: number | string | null,
    precision: number,
    thousands: string,
    decimal: string
  ) {
    if (number !== 0 && !number) return '';

    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    number = parseFloat(`${number}`).toFixed(precision);

    const parts = number.toString().split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const money = parts[1] ? decimal + parts[1] : '';

    return dollars + money;
  }

  switch (type) {
    case 'amount':
      return formatWithDelimiters(money, 2);
    case 'amount_no_decimals':
      return formatWithDelimiters(money, 0);
    case 'amount_with_comma_separator':
      return formatWithDelimiters(money, 2, '.', ',');
    case 'amount_no_decimals_with_comma_separator':
      return formatWithDelimiters(money, 0, '.', ',');
    case 'amount_with_space_separator_no_comma':
      return formatWithDelimiters(money, 2);
    case 'amount_no_decimals_with_space_separator':
      return formatWithDelimiters(money, 0, ' ', '.');
    default:
      return formatWithDelimiters(money, 2);
  }
};

const getTypeFormatMoney = (format: string) => {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  // eslint-disable-next-line no-template-curly-in-string
  const formatString = format || '${{amount}}';
  const matches = formatString.match(placeholderRegex);
  const match = matches && matches.length > 1 ? matches[1] : '';
  return match;
};
